import React, { memo, useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { dates, formats, validators } from "investira.sdk";
import {
  Button,
  Stack,
  InputAdornment,
  MenuItem,
  TextField,
  Icon,
  LoadingButton,
  Skeleton,
  ListItemText,
  Chip,
} from "investira.react.components";
import { VALIDATIONS } from "../../../const";
import { useRelatorioContext } from "../../../contexts/RelatorioContext";
import services from "../../../services";
import withResponseHandling from "../../../hoc/withResponseHandling";
import withSaveParams from "../../../hoc/withSaveParams";
import withMessage from "../../../hoc/withMessage";
import { CONFERENCIA_CUSTODIANTE_STATUS } from "../../../enums";

// 1) carteira
// 2) data
const VALIDATION_SCHEMA = Yup.object().shape({
  carteira_id: Yup.string().required(VALIDATIONS.REQUIRED),
  data: Yup.string().required(VALIDATIONS.REQUIRED).nullable(),
});

const ConferenciaCustodianteParams = memo((props) => {
  const { state, actions } = useRelatorioContext();
  const [datas, setDatas] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const storedParams = props.storedParams(
    "conferenciacustodiante",
    props.lastOutput,
  );

  const xInitialValues = {
    carteira_id: "",
    data: storedParams.data || null,
  };

  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (pValues, pActions) => {
      services.outputs.conferenciacustodiante(
        {
          ...pValues,
        },
        (rRes) => {
          const { output_cache_id } = rRes.data;
          navigate(`${location.pathname}/${output_cache_id}`);
          actions.handleNewConsultCancel();
          props.saveParams("conferenciacustodiante", pValues);
        },
        (rErr) => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        },
      );
    },
  });

  useEffect(() => {
    const xValues = storedParams;
    if (!validators.isEmpty(xValues)) {
      const { ...restValues } = xValues;
      formik.setValues({ ...xInitialValues, ...restValues });
    }
  }, []);

  useEffect(() => {
    if (state.isDialogOpen && formik.values.carteira_id !== "") {
      setDatas([]);

      formik.values.carteira_id &&
        services.conferenciasCustodiantes.datas(
          {
            carteira_id: formik.values.carteira_id,
          },
          (rRes) => {
            const xConferenciasDatas =
              rRes.data[`_${formik.values.carteira_id}`]?.datas ?? [];

            const xDatesFormatted = xConferenciasDatas.map(
              (xConferenciaData) => {
                return {
                  value: formats.formatDateCustom(
                    dates.toDate(xConferenciaData.data),
                    "YYYY-MM-DD",
                  ),
                  label: formats.formatDateCustom(
                    dates.toDate(xConferenciaData.data),
                    "DD/MM/YYYY",
                  ),
                  status: {
                    descricao:
                      CONFERENCIA_CUSTODIANTE_STATUS[xConferenciaData.status]
                        .descricao,
                    color:
                      xConferenciaData.status ===
                      CONFERENCIA_CUSTODIANTE_STATUS.AUS.id
                        ? "error"
                        : xConferenciaData.status ===
                          CONFERENCIA_CUSTODIANTE_STATUS.DIV.id
                        ? "warning"
                        : "success",
                  },
                };
              },
            );

            if (validators.isEmpty(xDatesFormatted)) {
              props.onMessageError(
                "Nenhuma conferência disponível para esta carteira",
              );
              return;
            }

            // Aplica a ordenação
            xDatesFormatted.sort((a, b) => {
              return new Date(a.value) - new Date(b.value);
            });

            setDatas(xDatesFormatted);

            const xStoredValues = storedParams;

            if (
              !validators.isEmpty(xStoredValues) &&
              formik.values.carteira_id === xStoredValues.carteira_id
            ) {
              const { data } = xStoredValues;
              formik.setFieldValue("data", data);
            } else {
              formik.setFieldValue("data", xDatesFormatted[0]?.value);
            }
          },
          (rErr) => {
            props.responseErrorHandling(rErr);
          },
        );
    }
  }, [state.isDialogOpen, formik.values.carteira_id]);

  const xCarteiras = useMemo(
    () =>
      props.carteiras
        .slice()
        .filter((xCarteira) => xCarteira.custodiante_carteira_id !== null) // filtra apenas carteiras que possuem tipo custodiante
        .sort((a, b) => a.carteira.localeCompare(b.carteira)),
    [props.carteiras],
  );

  const xStatus = useMemo(() => {
    return datas.find((xOption) => xOption.value === formik.values.data)
      ?.status;
  }, [datas, formik.values.data]);

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Stack sx={{ width: "500px", marginBottom: 2, py: 1 }} spacing={2}>
        <Stack flexGrow={1}>
          <TextField
            id="carteira_id"
            name="carteira_id"
            label="Carteira"
            variant="outlined"
            value={formik.values.carteira_id}
            onChange={formik.handleChange}
            select
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon iconName="wallet" size={21} />
                </InputAdornment>
              ),
            }}
          >
            {xCarteiras.map((xOption) => (
              <MenuItem key={xOption.carteira_id} value={xOption.carteira_id}>
                {xOption.carteira}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {formik.values.carteira_id && (
          <>
            <Stack direction="row" spacing={2}>
              <Stack flexGrow={1} flexBasis={1}>
                {validators.isEmpty(dates) ? (
                  <Skeleton variant="rounded" animation="wave" height={32} />
                ) : (
                  <TextField
                    id="data-posicao"
                    name="data"
                    label="Posição de"
                    variant="outlined"
                    value={formik.values.data}
                    onChange={(e) => {
                      formik.handleChange(e);
                    }}
                    sx={{
                      "& .MuiChip-root": {
                        display: "none",
                      },
                    }}
                    select
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon iconName="calendar" size={21} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: 3 }}>
                          {xStatus && (
                            <Chip
                              label={xStatus.descricao}
                              color={xStatus.color}
                              size="small"
                              sx={{ px: 1, display: "block !important" }}
                              variant="filled"
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(formik.errors.data)}
                    helperText={formik.errors.data}
                    disabled={datas.length <= 1}
                  >
                    {datas.map((xOption, xIndex) => (
                      <MenuItem key={xOption.value} value={xOption.value}>
                        <ListItemText>{xOption.label}</ListItemText>
                        <Chip
                          label={xOption.status.descricao}
                          color={xOption.status.color}
                          size="small"
                          sx={{ px: 1 }}
                          variant="filled"
                        />
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            </Stack>
          </>
        )}
        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={2}
        >
          <Button
            variant="outlined"
            type="button"
            color="primary"
            onClick={actions.handleNewConsultCancel}
          >
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={formik.isSubmitting}
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              validators.isEmpty(datas) ||
              validators.isEmpty(formik.values.carteira_id)
            }
          >
            Processar
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
});

export default withResponseHandling(
  withSaveParams(withMessage(ConferenciaCustodianteParams)),
);
